import { ApolloLink, Observable } from '@apollo/client/core';
import { inject, injectable } from 'inversify';
import {
  type Config,
  isNotUndefinedOrNull,
  type Logger,
  TYPES,
} from '@bizzdesign/sdk-internal';

import { Buffer } from 'buffer';

import { type AuthTokenProvider } from '@bizzdesign/sdk-auth';

import { Kind, print } from 'graphql';
import {
  isNotifyWebSocketWatchdogEventSubject,
  NOTIFY_SUBJECT_KEY,
  RESUBSCRIBE_ON_RECONNECT_KEY,
  type WebSocketWatchdogEvent,
  WebSocketWatchdogEventType,
} from './realtime-context';
import { type Observer } from 'rxjs';
import { generateUUID } from '@bizzdesign/sdk-internal/uuid';

// Check if running in a browser environment
const isBrowser = typeof window !== 'undefined';

// Use the native WebSocket in the browser or the ws library in Node.js
const WebSocketImpl: typeof WebSocket = isBrowser
  ? window.WebSocket
  : (require('ws') as typeof WebSocket);

export enum WebSocketMessageType {
  Start = 'start',
  ConnectionInit = 'connection_init',
  ConnectionAck = 'connection_ack',
  Error = 'error',
  StartAck = 'start_ack',
  Stop = 'stop',
  KeepAlive = 'ka',
}

interface WebSocketMessage {
  type: WebSocketMessageType; // Use enum for type
  id?: string;
  payload?: WebSocketPayload | ErrorPayload;
}

interface WebSocketPayload {
  data?: string;
  extensions?: {
    authorization: AuthorizationHeaders;
  };
  headers?: AuthorizationHeaders;
}

interface ErrorPayload {
  errors: { message: string }[];
}

// Define the structure of the headers
interface AuthorizationHeaders {
  Authorization: string;
  host: string;
}

@injectable()
export class AppSyncWebSocketLink extends ApolloLink {
  private ws: WebSocket | null = null;
  private connected = false;
  private disposed = false;
  /**
   * Track active subscriptions by storing the observer and the original operation, allowing us to restart it if necessary.
   */
  private subscriptions = new Map<
    string,
    {
      observer: Observer<ApolloLink.Result>;
      operation: Omit<ApolloLink.Operation, 'client'>;
      resubscribeOnReconnect?: boolean;
    }
  >();
  private connectionAckReceived = false;
  /**
   * Whether any WebSocket for this link has ever been acknowledged. Unlike
   * {@link connectionAckReceived} this is never reset per connection attempt, so it
   * distinguishes the first ACK of the link's lifetime from a reconnect's ACK.
   */
  private hasEverBeenAcked = false;
  private connecting: Promise<void> | null = null;
  private readonly MAX_RETRY_ATTEMPTS = 5;
  private readonly RECONNECT_BASE_DELAY_MS = 500;
  private readonly RECONNECT_BACKOFF_MAX_MS = 10_000;
  private retryAttempts = 0;
  public static readonly MAX_CONNECTION_RETRIES = 15;
  public static readonly TIMEOUT_CONNECTION = 500;
  private lastMessageAt = 0;
  private heartbeatWatchdogTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * AppSync heartbeat ('ka') interval
   */
  public static readonly HEARTBEAT_INTERVAL_MS = 60_000;
  /**
   * Allow a 10% grace window beyond the expected heartbeat interval before
   * considering the socket stale.
   */
  private static readonly HEARTBEAT_TIMEOUT_MS =
    (AppSyncWebSocketLink.HEARTBEAT_INTERVAL_MS * 11) / 10;
  /**
   * The frequency used to check the AppSync heartbeat for staleness.
   */
  private static readonly WATCHDOG_INTERVAL_MS = 5_000;

  constructor(
    @inject(TYPES.AuthTokenProvider)
    private authTokenProvider: AuthTokenProvider,
    @inject(TYPES.Logger) private logger: Logger,
    @inject(TYPES.Config) private config: Config,
  ) {
    super();
    this.onOpen = this.onOpen.bind(this);

    if (isBrowser && this.supportsWindowEventListener()) {
      // adds event listener for WindowEventHandlersEventMap
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  private isWebsocketPayload(
    payload: WebSocketPayload | ErrorPayload | undefined,
  ): payload is WebSocketPayload {
    return isNotUndefinedOrNull(payload) && 'data' in payload;
  }

  async generateWebSocketUrl(): Promise<string> {
    const headers = await this.getAuthorizationHeaders();
    const encodedHeader = Buffer.from(JSON.stringify(headers)).toString(
      'base64',
    );
    if (!this.config.graphqlRealtimeEndpoint) {
      throw new Error(
        '`graphqlRealtimeEndpoint` is required and should be set to AppSync realtime WebSocket URL',
      );
    }
    const url = new URL(
      `${this.config.graphqlRealtimeEndpoint}?header=${encodedHeader}&payload=e30=`,
    );
    url.protocol = 'wss:';
    return url.toString();
  }

  private async getAuthorizationHeaders(): Promise<AuthorizationHeaders> {
    const token = await this.authTokenProvider.getToken();
    return {
      Authorization: `Bearer ${token}`,
      host: new URL(this.config.graphqlEndpoint).host,
    };
  }

  private handleError(message: WebSocketMessage): void {
    const payload = message.payload as ErrorPayload;
    const errorMessages =
      payload.errors
        ?.map((error) => error.message)
        ?.filter(
          (message): message is string =>
            message !== null && message !== undefined,
        ) ?? [];
    const combinedError =
      errorMessages.length > 0 ? errorMessages.join('; ') : 'Unknown error';

    this.logger.error({
      context: 'AppSyncWebSocketLink',
      msg: 'AppSync subscription error',
      error: combinedError,
    });

    const sub = isNotUndefinedOrNull(message.id)
      ? this.subscriptions.get(message.id)
      : undefined;
    sub?.observer.error?.(new Error(combinedError));
  }

  private onMessage = (event: MessageEvent): void => {
    let message: WebSocketMessage;

    try {
      message = JSON.parse(event.data);
    } catch (error) {
      this.logger.error({
        context: 'AppSyncWebSocketLink',
        msg: 'Error parsing WebSocket message',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return;
    }

    this.logger.debug({
      context: 'AppSyncWebSocketLink',
      msg: 'Received WebSocket message',
      payload: message,
    });
    this.lastMessageAt = Date.now(); // any message counts as a KeepAlive
    switch (message.type) {
      case WebSocketMessageType.StartAck:
        this.logger.debug({
          context: 'AppSyncWebSocketLink',
          msg: 'Subscription started:',
          payload: message.id,
        });
        break;

      case WebSocketMessageType.ConnectionAck: {
        const isReconnect = this.hasEverBeenAcked;
        this.connectionAckReceived = true;
        this.hasEverBeenAcked = true;
        this.logger.debug({
          context: 'AppSyncWebSocketLink',
          msg: 'AppSync WebSocket connection established',
        });
        // Once the new connection is acknowledged, restart all opted-in subscriptions.
        void this.resubscribeOptedIn().catch((err: unknown) =>
          this.logger.error({
            context: 'AppSyncWebSocketLink',
            msg: 'Resubscribe failed',
            error: err,
          }),
        );
        // Only a reconnect can have missed events worth invalidating cached data for.
        // Mirrors the `wasConnected` guard in resetConnectionState().
        if (isReconnect) {
          this.notify({
            eventType: WebSocketWatchdogEventType.ConnectionRestored,
          });
        }
        break;
      }

      case WebSocketMessageType.Error:
        this.handleError(message);
        break;
      case WebSocketMessageType.KeepAlive:
        break;
      default:
        if (isNotUndefinedOrNull(message.id)) {
          const sub = this.subscriptions.get(message.id);
          if (sub && typeof sub.observer.next === 'function') {
            const payload = message.payload;
            if (this.isWebsocketPayload(payload)) {
              sub.observer.next(
                (payload ?? message) as unknown as ApolloLink.Result,
              );
            } else {
              this.handleError(message);
            }
          }
        }
    }
  };

  private resetConnectionState(): void {
    // Only notify on transitions from "connected" to "disconnected" so we
    // don't fire ConnectionLost during the initial connect cycle (when
    // connectionAckReceived was never true yet).
    const wasConnected = this.connectionAckReceived;
    this.connected = false;
    this.connectionAckReceived = false;
    if (wasConnected) {
      this.notify({
        eventType: WebSocketWatchdogEventType.ConnectionLost,
      });
    }
  }

  private onClose = (): void => {
    this.logger.debug({
      context: 'AppSyncWebSocketLink',
      msg: 'WebSocket closed, attempting to reconnect...',
    });
    this.stopHeartbeatWatchdog();
    this.resetConnectionState();
    this.reconnect();
  };

  private onErrorMethod = (event: Event): void => {
    this.logger.error({
      context: 'AppSyncWebSocketLink',
      msg: 'WebSocket Error',
      error: event,
    });
    this.stopHeartbeatWatchdog();
    this.resetConnectionState();
    this.reconnect();
  };

  private async onOpen(): Promise<void> {
    this.retryAttempts = 0;
    this.lastMessageAt = Date.now();
    this.startHeartbeatWatchdog();
    const headers = await this.getAuthorizationHeaders();

    this.logger.debug({
      context: 'AppSyncWebSocketLink',
      msg: 'Connected to AppSync WebSocket',
    });
    this.sendMessage({
      type: WebSocketMessageType.ConnectionInit,
      payload: { headers },
    });
    this.connected = true;
  }

  async connect(): Promise<void> {
    if (this.connected || this.disposed) return;
    // Prevent multiple simultaneous connection attempts
    if (isNotUndefinedOrNull(this.connecting)) {
      return this.connecting;
    }
    // ensure ACK state is fresh for each connection attempt
    this.connectionAckReceived = false;

    this.connecting = (async (): Promise<void> => {
      const wsUrl = await this.generateWebSocketUrl();
      // A dispose() may have run while the URL was being generated; abort so we
      // don't resurrect the socket after teardown.
      if (this.disposed) return;
      this.ws = new WebSocketImpl(wsUrl, ['graphql-ws']);

      this.ws.onopen = this.onOpen;
      this.ws.onmessage = this.onMessage;
      this.ws.onerror = this.onErrorMethod;
      this.ws.onclose = this.onClose;
    })();

    return this.connecting;
  }

  /**
   * Computes the delay before the next reconnection attempt using a linear backoff strategy.
   * The computed delay is always:
   * - At least the base delay (`RECONNECT_BASE_DELAY_MS`).
   * - No greater than the maximum cap (`RECONNECT_BACKOFF_MAX_MS`).
   *
   * @param attempt - The current retry attempt number.
   * @returns The computed backoff delay in milliseconds.
   */
  private computeBackoffDelay(attempt: number): number {
    return Math.max(
      this.RECONNECT_BASE_DELAY_MS,
      Math.min(
        this.RECONNECT_BACKOFF_MAX_MS,
        this.RECONNECT_BASE_DELAY_MS * attempt,
      ),
    );
  }

  private reconnect(): void {
    if (!this.connected) {
      this.disconnect();

      this.retryAttempts++;

      if (this.retryAttempts <= this.MAX_RETRY_ATTEMPTS) {
        const delay = this.computeBackoffDelay(this.retryAttempts);
        this.logger.debug({
          context: 'AppSyncWebSocketLink',
          msg: `Attempting reconnect in ${delay}ms (attempt ${this.retryAttempts}/${this.MAX_RETRY_ATTEMPTS})`,
        });

        setTimeout(() => {
          this.connect().catch((error) => {
            this.logger.error({
              context: 'AppSyncWebSocketLink',
              msg: 'Reconnection attempt failed',
              error,
            });
            if (!this.connected) {
              this.reconnect();
            }
          });
        }, delay);
      } else {
        this.logger.error({
          context: 'AppSyncWebSocketLink',
          msg: 'Max reconnection attempts reached',
        });
        for (const { observer } of this.subscriptions.values()) {
          observer.error?.(
            new Error('WebSocket connection failed after max retries'),
          );
        }
        this.subscriptions.clear();
        this.disconnect();
      }
    }
  }

  /**
   * Releases every resource held by this link so it can be garbage collected:
   * stops the heartbeat watchdog, tears down the active WebSocket, clears tracked
   * subscriptions and removes the browser online/offline listeners.
   */
  dispose(): void {
    this.disposed = true;
    this.stopHeartbeatWatchdog();
    for (const { observer } of this.subscriptions.values()) {
      observer.complete?.();
    }
    this.disconnect();
    this.subscriptions.clear();
    if (isBrowser && this.supportsWindowEventListener()) {
      globalThis.removeEventListener('online', this.handleOnline);
      globalThis.removeEventListener('offline', this.handleOffline);
    }
  }

  private disconnect(): void {
    if (isNotUndefinedOrNull(this.ws)) {
      // Remove all listeners before closing
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;

      this.ws.close();
      this.ws = null;
    }
    this.stopHeartbeatWatchdog();
    this.connecting = null;
  }

  private startHeartbeatWatchdog(): void {
    this.stopHeartbeatWatchdog();
    this.heartbeatWatchdogTimer = setInterval(
      () => this.heartbeatCheck(),
      AppSyncWebSocketLink.WATCHDOG_INTERVAL_MS,
    );
  }

  private stopHeartbeatWatchdog(): void {
    if (this.heartbeatWatchdogTimer) {
      clearInterval(this.heartbeatWatchdogTimer);
      this.heartbeatWatchdogTimer = null;
    }
  }

  private heartbeatCheck(): void {
    if (!this.ws || this.ws.readyState !== WebSocketImpl.OPEN) return;
    const idleFor = Date.now() - this.lastMessageAt;

    if (idleFor > AppSyncWebSocketLink.HEARTBEAT_TIMEOUT_MS) {
      this.logger.warn({
        context: 'AppSyncWebSocketLink',
        msg: 'Stale WebSocket detected (no frames / ka). Forcing reconnect.',
        payload: { idleFor },
      });
      this.forceReconnect();
    }
  }

  private handleOnline = (): void => {
    this.logger.debug({
      context: 'AppSyncWebSocketLink',
      msg: 'Browser online detected',
    });
    // Try an immediate reconnect once; fall back to the standard backoff loop if it fails.
    this.forceReconnect();
  };

  private handleOffline = (): void => {
    this.logger.warn({
      context: 'AppSyncWebSocketLink',
      msg: 'Browser offline detected',
    });
  };

  /**
   * When the browser notifies that the connection has been restored, or when the heartbeat
   * indicates a potentially half-open WebSocket, we immediately invoke connect() to quickly
   * re-establish the connection.
   * If this attempt fails, we then proceed with the standard reconnect() loop.
   */
  private forceReconnect(): void {
    this.resetConnectionState();
    this.stopHeartbeatWatchdog();
    this.retryAttempts = 0;
    this.disconnect();
    this.connect().catch((error) => {
      this.logger.error({
        context: 'AppSyncWebSocketLink',
        msg: 'Force reconnect failed',
        error,
      });
      if (!this.connected) {
        this.reconnect();
      }
    });
  }

  private sendMessage(message: WebSocketMessage): void {
    if (
      isNotUndefinedOrNull(this.ws) &&
      this.ws.readyState === WebSocketImpl.OPEN
    ) {
      this.ws.send(JSON.stringify(message));
    } else {
      this.logger.warn({
        context: 'AppSyncWebSocketLink',
        msg: 'Attempted to send message to a closed WebSocket',
      });
    }
  }

  /**
   * restart all opted-in subscriptions after a successful reconnect+ACK
   */
  private async resubscribeOptedIn(): Promise<void> {
    if (!this.connectionAckReceived || !this.connected) return;
    const headers = await this.getAuthorizationHeaders();
    for (const [
      id,
      { operation, resubscribeOnReconnect },
    ] of this.subscriptions.entries()) {
      const shouldResubscribe: boolean = resubscribeOnReconnect === true;
      if (shouldResubscribe) {
        this.logger.debug({
          context: 'AppSyncWebSocketLink',
          msg: 'Re-subscribing operation',
          payload: { id },
        });
        this.sendMessage({
          id,
          type: WebSocketMessageType.Start,
          payload: {
            data: JSON.stringify({
              query: print(operation.query),
              variables: operation.variables,
            }),
            extensions: { authorization: headers },
          },
        });
      }
    }
  }

  override request(
    operation: Omit<ApolloLink.Operation, 'client'>,
  ): Observable<ApolloLink.Result> {
    return new Observable<ApolloLink.Result>((observer) => {
      let cleanup: (() => void) | undefined;
      let connectionRetries = 0;

      const tryRequest = async (): Promise<void> => {
        // Ensure connection is established before proceeding
        if (!this.connected) {
          await this.connect();
        }

        if (!this.connectionAckReceived) {
          if (
            connectionRetries >= AppSyncWebSocketLink.MAX_CONNECTION_RETRIES
          ) {
            this.logger.error({
              context: 'AppSyncWebSocketLink',
              msg: 'Connection acknowledgment timeout.',
              payload: connectionRetries,
            });
            return;
          }
          connectionRetries++;
          await new Promise((resolve) =>
            setTimeout(resolve, AppSyncWebSocketLink.TIMEOUT_CONNECTION),
          );
          await tryRequest();
          return;
        }

        const isSubscription = operation.query.definitions.some(
          (definition) =>
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === 'subscription',
        );

        if (!isSubscription) {
          observer.error(
            new Error(
              'Only subscription operations are supported over WebSocket',
            ),
          );
          return;
        }

        const id = generateUUID();
        const resubscribeOnReconnect =
          true === operation.getContext()?.[RESUBSCRIBE_ON_RECONNECT_KEY];
        this.subscriptions.set(id, {
          observer,
          operation,
          resubscribeOnReconnect,
        });

        const headers = await this.getAuthorizationHeaders();

        this.sendMessage({
          id,
          type: WebSocketMessageType.Start,
          payload: {
            data: JSON.stringify({
              query: print(operation.query),
              variables: operation.variables,
            }),
            extensions: {
              authorization: headers,
            },
          },
        });

        cleanup = (): void => {
          this.sendMessage({ id, type: WebSocketMessageType.Stop });
          this.subscriptions.delete(id);
        };
      };

      tryRequest().catch((error): void => {
        this.logger.error({
          context: 'AppSyncWebSocketLink',
          msg: 'Error in tryRequest',
          error: error,
        });
        observer.error(new Error('Failed to execute request'));
      });

      return () => cleanup?.();
    });
  }

  private notify(event: WebSocketWatchdogEvent): void {
    const notified = new Set<unknown>();
    for (const [, { operation }] of this.subscriptions) {
      const ctx = operation.getContext?.();
      const candidate = ctx?.[NOTIFY_SUBJECT_KEY] as unknown;
      if (
        !notified.has(candidate) &&
        isNotifyWebSocketWatchdogEventSubject(candidate)
      ) {
        notified.add(candidate);
        try {
          candidate.next(event);
        } catch (err) {
          this.logger.error({
            context: 'AppSyncWebSocketLink',
            msg: 'cacheInvalidationSubject.next failed',
            error: err,
          });
        }
      }
    }
  }

  private supportsWindowEventListener(): boolean {
    try {
      return isBrowser && typeof window.addEventListener === 'function';
    } catch {
      return false;
    }
  }
}
